import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';

// ─── Constantes ───────────────────────────────────────────────────────────────
const TIPO_CONFIG = {
  llamada: {
    label: 'Llamar',
    accion: 'Realizar llamada',
    icon: 'Phone',
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    btnBg: 'bg-blue-600 hover:bg-blue-700',
  },
  whatsapp: {
    label: 'Enviar WhatsApp',
    accion: 'Enviar mensaje por WhatsApp',
    icon: 'MessageCircle',
    color: 'text-green-600',
    bg: 'bg-green-50',
    border: 'border-green-200',
    btnBg: 'bg-green-600 hover:bg-green-700',
  },
  email: {
    label: 'Enviar email',
    accion: 'Enviar correo electrónico',
    icon: 'Mail',
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    btnBg: 'bg-purple-600 hover:bg-purple-700',
  },
  cita: {
    label: 'Cita',
    accion: 'Confirmar o asistir a cita',
    icon: 'Calendar',
    color: 'text-orange-600',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    btnBg: 'bg-orange-600 hover:bg-orange-700',
  },
  seguimiento: {
    label: 'Dar seguimiento',
    accion: 'Realizar seguimiento al prospecto',
    icon: 'RefreshCw',
    color: 'text-cyan-600',
    bg: 'bg-cyan-50',
    border: 'border-cyan-200',
    btnBg: 'bg-cyan-600 hover:bg-cyan-700',
  },
  mensaje: {
    label: 'Enviar mensaje',
    accion: 'Enviar mensaje al contacto',
    icon: 'MessageSquare',
    color: 'text-indigo-600',
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    btnBg: 'bg-indigo-600 hover:bg-indigo-700',
  },
};

const getTipoConfig = (tipo) =>
  TIPO_CONFIG?.[tipo] || {
    label: tipo || 'Actividad',
    accion: 'Realizar actividad',
    icon: 'Activity',
    color: 'text-gray-600',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    btnBg: 'bg-gray-600 hover:bg-gray-700',
  };

const formatFecha = (fecha) => {
  if (!fecha) return '—';
  const d = new Date(fecha + 'T00:00:00');
  return d?.toLocaleDateString('es-MX', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatHora = (hora) => {
  if (!hora) return null;
  return hora?.slice(0, 5);
};

// ─── Tarjeta de Recordatorio ──────────────────────────────────────────────────
const RecordatorioCard = ({
  activity,
  isVencida,
  onUpdateStatus,
  onEnviarWhatsApp,
  onAbrirContacto,
  onAgendar,
}) => {
  const tipo = getTipoConfig(activity?.tipo);
  const hasWA =
    activity?.contact?.whatsapp || activity?.contact?.telefono;
  const hasEmail = activity?.contact?.email;
  const hora = formatHora(activity?.hora);

  const handleWhatsApp = () => {
    if (!activity?.contact) return;
    const numero = (activity?.contact?.whatsapp || activity?.contact?.telefono || '')?.replace(/\D/g,'');
    if (!numero) return;
    if (onEnviarWhatsApp) {
      onEnviarWhatsApp(activity?.contact, activity?.notas);
    } else {
      window.open(`https://wa.me/${numero}`, '_blank');
    }
  };

  const handleEmail = () => {
    if (!hasEmail) return;
    window.open(`mailto:${activity?.contact?.email}`, '_blank');
  };

  const handleLlamar = () => {
    const numero = (activity?.contact?.telefono || activity?.contact?.whatsapp || '')?.replace(/\D/g,'');
    if (!numero) return;
    window.open(`tel:${numero}`, '_blank');
  };

  return (
    <div
      className={`bg-card rounded-xl border p-4 hover:shadow-md transition-shadow ${
        isVencida ? 'border-red-200 bg-red-50/30' : 'border-border'
      }`}
    >
      {/* Header: tipo + fecha */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div
            className={`w-9 h-9 rounded-full ${tipo?.bg} border ${tipo?.border} flex items-center justify-center flex-shrink-0`}
          >
            <Icon name={tipo?.icon} size={16} className={tipo?.color} />
          </div>
          <div>
            <p className={`text-sm font-bold ${tipo?.color}`}>{tipo?.label}</p>
            {activity?.contact?.nombre && (
              <p className="text-xs text-muted-foreground font-medium">
                {activity?.contact?.nombre}
                {activity?.contact?.empresa
                  ? ` · ${activity?.contact?.empresa}`
                  : ''}
              </p>
            )}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p
            className={`text-xs font-semibold ${
              isVencida ? 'text-red-600' : 'text-foreground'
            }`}
          >
            {formatFecha(activity?.fecha)}
          </p>
          {hora && (
            <p className="text-xs text-muted-foreground">{hora}</p>
          )}
        </div>
      </div>
      {/* Acción sugerida */}
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded-lg mb-3 ${tipo?.bg} border ${tipo?.border}`}
      >
        <Icon name="Zap" size={12} className={tipo?.color} />
        <p className={`text-xs font-semibold ${tipo?.color}`}>
          Acción: {tipo?.accion}
        </p>
      </div>
      {/* Notas */}
      {activity?.notas && (
        <p className="text-xs text-muted-foreground bg-muted/40 rounded-lg p-2 mb-3 line-clamp-2">
          {activity?.notas}
        </p>
      )}
      {/* Responsable */}
      {activity?.responsable_user?.full_name && (
        <p className="text-xs text-muted-foreground mb-3 flex items-center gap-1">
          <Icon name="User" size={11} />
          {activity?.responsable_user?.full_name}
        </p>
      )}
      {/* Botones de acción */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Acción principal según tipo */}
        {activity?.tipo === 'llamada' && (
          <button
            onClick={handleLlamar}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium transition-colors"
          >
            <Icon name="Phone" size={12} />
            Llamar
          </button>
        )}
        {(activity?.tipo === 'whatsapp' || activity?.tipo === 'mensaje') &&
          hasWA && (
            <button
              onClick={handleWhatsApp}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-medium transition-colors"
            >
              <Icon name="MessageCircle" size={12} />
              Abrir WhatsApp
            </button>
          )}
        {activity?.tipo === 'email' && hasEmail && (
          <button
            onClick={handleEmail}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium transition-colors"
          >
            <Icon name="Mail" size={12} />
            Enviar email
          </button>
        )}
        {/* WhatsApp secundario si no es el tipo principal */}
        {activity?.tipo !== 'whatsapp' &&
          activity?.tipo !== 'mensaje' &&
          hasWA && (
            <button
              onClick={handleWhatsApp}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-50 hover:bg-green-100 text-green-700 border border-green-200 text-xs font-medium transition-colors"
            >
              <Icon name="MessageCircle" size={12} />
              WhatsApp
            </button>
          )}
        {/* Agendar trabajo */}
        {onAgendar && activity?.contact && (
          <button
            onClick={() => onAgendar(activity?.contact, activity)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary text-xs font-medium transition-colors"
          >
            <Icon name="CalendarPlus" size={12} />
            Agendar trabajo
          </button>
        )}
        {/* Abrir contacto */}
        {activity?.contact && onAbrirContacto && (
          <button
            onClick={() => onAbrirContacto(activity?.contact)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-muted hover:bg-muted/80 text-foreground text-xs font-medium transition-colors"
          >
            <Icon name="User" size={12} />
            Ver contacto
          </button>
        )}
        {/* Marcar realizada */}
        <button
          onClick={() => onUpdateStatus(activity?.id, 'realizado')}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary text-xs font-medium transition-colors"
        >
          <Icon name="CheckCircle" size={12} />
          Realizada
        </button>
        {/* Cancelar */}
        <button
          onClick={() => onUpdateStatus(activity?.id, 'cancelado')}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 text-xs font-medium transition-colors"
        >
          <Icon name="XCircle" size={12} />
          Cancelar
        </button>
      </div>
    </div>
  );
};

// ─── Encabezado de sección ────────────────────────────────────────────────────
const SectionHeader = ({ title, count, icon, colorClass, emptyLabel }) => (
  <div className="flex items-center gap-3 mb-4">
    <div
      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold ${colorClass}`}
    >
      <Icon name={icon} size={14} />
      {title}
      {count > 0 && (
        <span className="ml-1 bg-white/40 rounded-full px-1.5 py-0.5 text-xs font-bold">
          {count}
        </span>
      )}
    </div>
    <div className="flex-1 h-px bg-border" />
    {count === 0 && emptyLabel && (
      <span className="text-xs text-muted-foreground">{emptyLabel}</span>
    )}
  </div>
);

// ─── Componente principal ─────────────────────────────────────────────────────
const RecordatoriosView = ({
  activities,
  loading,
  currentUserId,
  onUpdateStatus,
  onEnviarWhatsApp,
  onAbrirContacto,
  onAgendar,
}) => {
  // Default: "mis" para que cada trabajador vea sus propias tareas
  const [filtro, setFiltro] = useState('mis');

  const today = new Date()?.toISOString()?.split('T')?.[0];
  const next7 = new Date();
  next7?.setDate(next7?.getDate() + 7);
  const next7Str = next7?.toISOString()?.split('T')?.[0];

  const filtered = useMemo(() => {
    return (activities || [])?.filter((act) => {
      if (act?.estado !== 'pendiente') return false;
      if (filtro === 'mis' && act?.responsable !== currentUserId) return false;
      return true;
    });
  }, [activities, filtro, currentUserId]);

  const hoy = useMemo(
    () => filtered?.filter((a) => a?.fecha === today),
    [filtered, today]
  );
  const proximos = useMemo(
    () =>
      filtered?.filter((a) => a?.fecha > today && a?.fecha <= next7Str),
    [filtered, today, next7Str]
  );
  const vencidos = useMemo(
    () => filtered?.filter((a) => a?.fecha < today),
    [filtered, today]
  );

  const totalPendientes = hoy?.length + proximos?.length + vencidos?.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Barra de resumen de notificaciones */}
      {filtro === 'mis' && (
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div
            className={`rounded-xl border p-4 flex items-center gap-3 ${
              hoy?.length > 0
                ? 'bg-yellow-50 border-yellow-200' :'bg-card border-border'
            }`}
          >
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                hoy?.length > 0 ? 'bg-yellow-100' : 'bg-muted'
              }`}
            >
              <Icon
                name="Sun"
                size={18}
                className={hoy?.length > 0 ? 'text-yellow-600' : 'text-muted-foreground'}
              />
            </div>
            <div>
              <p
                className={`text-2xl font-bold ${
                  hoy?.length > 0 ? 'text-yellow-700' : 'text-foreground'
                }`}
              >
                {hoy?.length}
              </p>
              <p className="text-xs text-muted-foreground">Mis tareas de hoy</p>
            </div>
          </div>
          <div className="bg-card rounded-xl border border-border p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
              <Icon name="CalendarDays" size={18} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{proximos?.length}</p>
              <p className="text-xs text-muted-foreground">Próximas</p>
            </div>
          </div>
          <div
            className={`rounded-xl border p-4 flex items-center gap-3 ${
              vencidos?.length > 0
                ? 'bg-red-50 border-red-200' :'bg-card border-border'
            }`}
          >
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                vencidos?.length > 0 ? 'bg-red-100' : 'bg-muted'
              }`}
            >
              <Icon
                name="AlertTriangle"
                size={18}
                className={
                  vencidos?.length > 0 ? 'text-red-600' : 'text-muted-foreground'
                }
              />
            </div>
            <div>
              <p
                className={`text-2xl font-bold ${
                  vencidos?.length > 0 ? 'text-red-700' : 'text-foreground'
                }`}
              >
                {vencidos?.length}
              </p>
              <p className="text-xs text-muted-foreground">Vencidas</p>
            </div>
          </div>
        </div>
      )}
      {/* Filtro */}
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center bg-muted rounded-lg p-1">
          <button
            onClick={() => setFiltro('mis')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              filtro === 'mis' ?'bg-card shadow-sm text-foreground' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Mis recordatorios
          </button>
          <button
            onClick={() => setFiltro('todos')}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              filtro === 'todos'
                ? 'bg-card shadow-sm text-foreground' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Todos
          </button>
        </div>
        <div className="text-sm text-muted-foreground">
          {totalPendientes} pendiente{totalPendientes !== 1 ? 's' : ''}
        </div>
      </div>
      <div className="space-y-8">
        {/* ── Mis tareas de hoy ── */}
        <div>
          <SectionHeader
            title="Mis tareas de hoy"
            count={hoy?.length}
            icon="Sun"
            colorClass="bg-yellow-100 text-yellow-700"
            emptyLabel="¡Sin tareas para hoy!"
          />
          {hoy?.length === 0 ? (
            <div className="text-center py-8 bg-card rounded-xl border border-border border-dashed">
              <Icon
                name="CheckCircle"
                size={28}
                className="text-green-400 mx-auto mb-2"
              />
              <p className="text-sm text-muted-foreground">
                Sin recordatorios para hoy
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {hoy?.map((act) => (
                <RecordatorioCard
                  key={act?.id}
                  activity={act}
                  isVencida={false}
                  onUpdateStatus={onUpdateStatus}
                  onEnviarWhatsApp={onEnviarWhatsApp}
                  onAbrirContacto={onAbrirContacto}
                  onAgendar={onAgendar}
                />
              ))}
            </div>
          )}
        </div>

        {/* ── Próximas ── */}
        <div>
          <SectionHeader
            title="Próximas"
            count={proximos?.length}
            icon="CalendarDays"
            colorClass="bg-blue-100 text-blue-700"
            emptyLabel="Sin actividades próximas"
          />
          {proximos?.length === 0 ? (
            <div className="text-center py-8 bg-card rounded-xl border border-border border-dashed">
              <Icon
                name="Calendar"
                size={28}
                className="text-muted-foreground mx-auto mb-2 opacity-40"
              />
              <p className="text-sm text-muted-foreground">
                Sin recordatorios próximos
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {proximos?.map((act) => (
                <RecordatorioCard
                  key={act?.id}
                  activity={act}
                  isVencida={false}
                  onUpdateStatus={onUpdateStatus}
                  onEnviarWhatsApp={onEnviarWhatsApp}
                  onAbrirContacto={onAbrirContacto}
                  onAgendar={onAgendar}
                />
              ))}
            </div>
          )}
        </div>

        {/* ── Vencidas ── */}
        <div>
          <SectionHeader
            title="Vencidas"
            count={vencidos?.length}
            icon="AlertTriangle"
            colorClass="bg-red-100 text-red-700"
            emptyLabel="Sin actividades vencidas"
          />
          {vencidos?.length === 0 ? (
            <div className="text-center py-8 bg-card rounded-xl border border-border border-dashed">
              <Icon
                name="CheckCircle"
                size={28}
                className="text-green-400 mx-auto mb-2"
              />
              <p className="text-sm text-muted-foreground">
                Sin actividades vencidas
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {vencidos?.map((act) => (
                <RecordatorioCard
                  key={act?.id}
                  activity={act}
                  isVencida
                  onUpdateStatus={onUpdateStatus}
                  onEnviarWhatsApp={onEnviarWhatsApp}
                  onAbrirContacto={onAbrirContacto}
                  onAgendar={onAgendar}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecordatoriosView;
